export interface ResovlerOpts {
	transform?: Function
	immediate?: boolean
	query?: Object
	onError?: Function
	tryBefore?: {
		resolver: string
		prop?: string
	}
}

export interface APIResolverOptions {
	url: string
	dataKey?: false
	watch?: boolean
	query?: object | Function
	transform?: Function
	pick?: string[]
	frozen?: string[]
	errors?: {[key: number]: string|Object}
	preload?: boolean
	tryBefore?: {
		resolver: string
		prop?: string
	}
}

class ApiResolver {
	private name: string
	private url: string
	private dataKey: false | string
	private watch: boolean
	private transform: Function | undefined
	private frozen: string[]
	private pick: string[]
	private immediate: boolean = true
	private initialQuery: Object | undefined
	private errors: {[key: number]: string} | undefined
	private status: Ref<'idle'|'pending'|'success'|'error'>
	private error: Ref
	private q: Ref
	private tryBefore: any
	private urlAndQuery = computed(() => {
		let url = String(this.url)
		let query = this.query.value ? {...this.query.value} : {}
		Object.keys(query).forEach((key: string) => {
			if (url.indexOf('{'+key+'}') > -1) {
				url = url.replace('{' + key + '}', query[key])
				delete query[key]
			}
		})
		return { url, query }
	})
	public query = computed({
		get: ()=> {
			return this.q.value
		},
		set: (value: any) => {
			if (!value && this.initialQuery)
				value = this.initialQuery
			this.frozen.forEach((v: string) => {
				if (value)
					value[v] = this.q.value?.hasOwnProperty(v)
						? this.q.value[v]
						: this.initialQuery?.hasOwnProperty(v)
							? this.initialQuery[v]
							: undefined
			})
			if (JSON.stringify(this.q.value ? {...this.q.value} : {}) !== JSON.stringify(value ? {...value} : {}))
				this.q.value = value ? {...value} : undefined
		}
	})

	constructor(name: string, opts: APIResolverOptions) {
		this.name = name
		this.url = opts.url
		this.dataKey = opts.dataKey === false ? opts.dataKey : name
		this.watch = opts.watch === false ? false : true
		this.initialQuery = opts.query ? {...(typeof opts.query === 'function' ? opts.query() : opts.query)} : undefined
		this.frozen = opts.frozen ? [...opts.frozen] : []
		this.pick = Array.isArray(opts.pick) ? opts.pick : []
		this.errors = opts.errors ? {...opts.errors} : undefined
		this.status = ref('idle')
		this.error = ref(undefined)
		this.q = ref(this.initialQuery)
		this.query.value = this.initialQuery
		this.transform = opts.transform
		this.tryBefore = opts.tryBefore
	}

	private init(opts?: ResovlerOpts) {
		function getValue (value: Object, name?: string = undefined): any {
			let v = value ? {...value} : {}
			if (name)
				for (const bit of name.split('.')) {
					if (Object.keys(v).includes(bit))
						v = v[bit]
					else
						return undefined
				}
			return v
		}
		const data = this.dataKey ? useNuxtData(this.dataKey).data : ref<any>(undefined)
		if (!opts)
			opts = {}
		const tryBefore = opts.tryBefore || this.tryBefore
		if (opts.query) {
			this.query.value = opts.query
			opts.immediate = true
			if (tryBefore) {
				const before =  getValue(useLazyDataResolver(tryBefore.resolver, {immediate: false}).data.value, tryBefore.prop)
				if (Array.isArray(before)) {
					before.forEach((item:any) => {
						const c = Object.keys(this.query.value).map((f: string) => item[f] === opts.query[f]).includes(false)
						if (!c) {
							data.value = {...item}
							this.status.value = 'success'
							opts.immediate = false
						}
					})
				}
			}
		}

		if (this.status === 'pending')
			this.immediate = false
		else {
			this.immediate = opts.hasOwnProperty('immediate') ? Boolean(opts.immediate) : !data.value
		}
		if (this.status.value === 'success' && !tryBefore)
			this.status.value = 'idle'

		const refresh = async ()=> {
			this.status.value = 'pending'
			this.error.value = undefined
			await $fetch(
				this.urlAndQuery.value.url,
				{
					baseURL: this.url.startsWith('/') ? useRuntimeConfig().public.apiBase : undefined,
					query: this.urlAndQuery.value.query,
					onResponse: ({response}) => {
						if (response.ok) {
							if (response._data) {
								data.value = this.transform
									? this.transform(response._data)
									: response._data
							}
							if (Array.isArray(this.pick) && data.value) {
								this.pick.forEach((pick: string) => {
									if (Object.keys(data.value).includes(pick))
										delete data.value[pick]
								})
							}
							this.status.value = 'success'
						}
					},
					onResponseError: ({ response }) => {
						this.status.value = 'error'
						this.error.value = {
							status: response.status,
							statusText: response.statusText,
							url: response.url,
							data: {
								...response._data
							},
						}
						if (opts?.onError)
							opts?.onError(this.error.value)
						if (this.errors && !!this.errors[response.status]) {
							const e = useError()
							console.log(response.status, this.errors[response.status])
							e.value = typeof this.errors[response.status] === 'string'
								? {...this.error.value, message: this.errors[response.status]}
								: {...this.error.value, ...this.errors[response.status]}
						}
					}
				}
			)
		}
		return { data, refresh }
	}

	public generateLazyResolver(opts?: ResovlerOpts) {
		const { data, refresh } = this.init(opts)

		if (this.immediate) {
			this.immediate = false
			refresh()
		}
		if(this.watch)
			watch(
				this.query, (nv) => {
					refresh()
				}, { deep: true})

		return { data, status: this.status, query: this.query, error: this.error, refresh }
	}

	public async generateResolver(opts?: ResovlerOpts) {
		const { data, refresh } = this.init(opts)

		if (this.immediate) {
			this.immediate = false
			await refresh()
		}
		if(this.watch)
			watch(
				this.query, (nv) => {
					refresh()
				}, { deep: true})
		return { data, status: this.status, query: this.query, error: this.error, refresh }
	}
}

const apiResolveConfigs: {[id: string]: typeof ApiResolver} = {}


function findResolverConfig(key: string) {
	const c = useAppConfig().gsmaterials
	if (c.apiData?.hasOwnProperty(key)) {
		apiResolveConfigs[key] = new ApiResolver(key, c.apiData[key])
	}
}

export function useLazyDataResolver(key: string, opts?: ResovlerOpts){
	if (!Object.keys(apiResolveConfigs).includes(key))
		findResolverConfig(key)
	return apiResolveConfigs[key].generateLazyResolver(opts)
}

export async function useDataResolver(key: string, opts?: ResovlerOpts) {
	if (!Object.keys(apiResolveConfigs).includes(key))
		findResolverConfig(key)
	return await apiResolveConfigs[key].generateResolver(opts)
}
